import React from 'react';
import { X, AlertCircle, RefreshCw, DollarSign, Wifi, AlertTriangle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface TradingErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRetry: () => void;
  error: {
    type: 'insufficient_balance' | 'order_failed' | 'network_error' | 'validation_error' | 'unknown';
    message: string;
    details?: string;
  };
  orderDetails?: {
    side: 'buy' | 'sell';
    type: 'market' | 'limit' | 'stop';
    symbol: string;
    amount: number;
    price: number;
  };
}

const TradingErrorModal: React.FC<TradingErrorModalProps> = ({
  isOpen,
  onClose,
  onRetry,
  error,
  orderDetails
}) => {
  const { theme } = useTheme();

  if (!isOpen) return null;

  const getErrorIcon = () => {
    switch (error.type) {
      case 'insufficient_balance':
        return <DollarSign className="h-8 w-8 text-red-500" />;
      case 'network_error':
        return <Wifi className="h-8 w-8 text-orange-500" />;
      case 'order_failed':
        return <AlertCircle className="h-8 w-8 text-red-500" />;
      case 'validation_error':
        return <AlertTriangle className="h-8 w-8 text-yellow-500" />;
      default:
        return <AlertCircle className="h-8 w-8 text-red-500" />;
    }
  };

  const getErrorTitle = () => {
    switch (error.type) {
      case 'insufficient_balance':
        return 'Insufficient Balance';
      case 'network_error':
        return 'Connection Error';
      case 'order_failed':
        return 'Order Failed';
      case 'validation_error':
        return 'Invalid Order';
      default:
        return 'Trading Error';
    }
  };

  const getErrorDescription = () => {
    switch (error.type) {
      case 'insufficient_balance':
        return 'You don\'t have enough balance to place this trade. Please check your account balance and try again.';
      case 'network_error':
        return 'There was a problem connecting to the trading server. Please check your internet connection and try again.';
      case 'order_failed':
        return 'The order could not be processed. This might be due to market conditions or system issues.';
      case 'validation_error':
        return 'The order details are invalid. Please check your input and try again.';
      default:
        return 'An unexpected error occurred while processing your order.';
    }
  };

  const getRetryButtonText = () => {
    switch (error.type) {
      case 'insufficient_balance':
        return 'Check Balance & Retry';
      case 'network_error':
        return 'Retry Connection';
      case 'order_failed':
        return 'Try Again';
      case 'validation_error':
        return 'Fix & Retry';
      default:
        return 'Try Again';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${theme === 'dark' ? 'theme-bg-secondary' : 'bg-white'} rounded-lg p-6 w-full max-w-md mx-4 shadow-xl`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            {getErrorIcon()}
            <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'theme-text-primary' : 'text-gray-900'}`}>
              {getErrorTitle()}
            </h2>
          </div>
          <button
            onClick={onClose}
            className={`${theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Error Message */}
        <div className="mb-4">
          <p className={`text-sm ${theme === 'dark' ? 'theme-text-secondary' : 'text-gray-600'} mb-2`}>
            {getErrorDescription()}
          </p>
          <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
            <p className={`text-sm font-medium ${theme === 'dark' ? 'text-red-200' : 'text-red-800'}`}>
              {error.message}
            </p>
            {error.details && (
              <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-red-300' : 'text-red-600'}`}>
                {error.details}
              </p>
            )}
          </div>
        </div>

        {/* Order Details (if available) */}
        {orderDetails && (
          <div className={`mb-4 p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
            <h4 className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'theme-text-primary' : 'text-gray-900'}`}>
              Order Details
            </h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className={`${theme === 'dark' ? 'theme-text-quaternary' : 'text-gray-500'}`}>Side:</span>
                <span className={`ml-2 font-medium ${orderDetails.side === 'buy' ? 'text-green-500' : 'text-red-500'}`}>
                  {orderDetails.side.toUpperCase()}
                </span>
              </div>
              <div>
                <span className={`${theme === 'dark' ? 'theme-text-quaternary' : 'text-gray-500'}`}>Type:</span>
                <span className={`ml-2 font-medium ${theme === 'dark' ? 'theme-text-secondary' : 'text-gray-700'}`}>
                  {orderDetails.type.toUpperCase()}
                </span>
              </div>
              <div>
                <span className={`${theme === 'dark' ? 'theme-text-quaternary' : 'text-gray-500'}`}>Symbol:</span>
                <span className={`ml-2 font-medium ${theme === 'dark' ? 'theme-text-secondary' : 'text-gray-700'}`}>
                  {orderDetails.symbol}
                </span>
              </div>
              <div>
                <span className={`${theme === 'dark' ? 'theme-text-quaternary' : 'text-gray-500'}`}>Amount:</span>
                <span className={`ml-2 font-medium ${theme === 'dark' ? 'theme-text-secondary' : 'text-gray-700'}`}>
                  {orderDetails.amount}
                </span>
              </div>
              <div className="col-span-2">
                <span className={`${theme === 'dark' ? 'theme-text-quaternary' : 'text-gray-500'}`}>Price:</span>
                <span className={`ml-2 font-medium ${theme === 'dark' ? 'theme-text-secondary' : 'text-gray-700'}`}>
                  ${orderDetails.price.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
              theme === 'dark' 
                ? 'theme-text-secondary theme-bg-quaternary hover:theme-bg-quinary' 
                : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
            }`}
          >
            Close
          </button>
          <button
            onClick={onRetry}
            className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 ${
              error.type === 'insufficient_balance' 
                ? 'bg-blue-600 hover:bg-blue-700' 
                : error.type === 'network_error'
                ? 'bg-orange-600 hover:bg-orange-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            <RefreshCw className="h-4 w-4" />
            <span>{getRetryButtonText()}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TradingErrorModal;
